import { useState, useEffect, useCallback } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { IconCalendarEvent, IconAlertCircle, IconRefresh, IconPlus, IconTrash, IconClock, IconSettings, IconBriefcase } from '@tabler/icons-react';
import { api } from '../../../../api/Middleware';
import { API_URL, BUSINESS_HOURS_SETTINGS_URL } from '../../../../api/Urls';
import WorkingHoursModal, { getHoursChip, getServicesChip } from './WorkingHoursModal';

// =============================================================================
// SCHEDULING CALENDAR PICKER — Google Calendar config for scheduling nodes
// Services and working hours are configured inside WorkingHoursModal.
// =============================================================================
const SchedulingCalendarPicker = ({ agentId, integrations, config, onChange }) => {
  const [loading, setLoading] = useState(true);
  const [googleCalendars, setGoogleCalendars] = useState([]);
  const [error, setError] = useState(null);

  // Add professional form state
  const [newProfName, setNewProfName] = useState('');
  const [newProfCalendar, setNewProfCalendar] = useState('');

  // Working hours/services modal target: null | 'single' | prof.id
  const [hoursModalTarget, setHoursModalTarget] = useState(null);

  // Global slot duration (BusinessHoursSettings.appointment_duration) —
  // usado como placeholder do campo "Slot do agente" no modal.
  const [globalSlotDuration, setGlobalSlotDuration] = useState(30);

  const useMultipleCalendars = config?.use_multiple_calendars || false;
  const services = config?.services || [];
  const professionals = config?.professionals || [];

  useEffect(() => {
    api.get(BUSINESS_HOURS_SETTINGS_URL)
      .then((res) => {
        const data = res.data?.results?.[0] || res.data?.[0] || res.data;
        const val = data?.appointment_duration;
        if (typeof val === 'number' && val > 0) setGlobalSlotDuration(val);
      })
      .catch(() => { /* mantém default 30 */ });
  }, []);

  // Find google_calendar integration
  const gcIntegration = (integrations || []).find(
    (i) => (i.integration_type === 'google_calendar' || i.integration_type === 'google_workspace') && i.is_active
  );
  const integrationId = gcIntegration?.id;

  const fetchCalendars = useCallback(async () => {
    if (!agentId || !integrationId) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(
        `${API_URL}agents_flow/agents/${agentId}/integrations/${integrationId}/google-calendars/`
      );
      setGoogleCalendars(response.data.google_calendars || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao carregar calendários');
    } finally {
      setLoading(false);
    }
  }, [agentId, integrationId]);

  useEffect(() => {
    if (integrationId) fetchCalendars();
    else setLoading(false);
  }, [integrationId, fetchCalendars]);

  // -- Helpers --
  const generateId = () => Date.now().toString(36) + Math.random().toString(36).substr(2);

  const getCalendarName = (calendarId) => {
    const cal = googleCalendars.find((c) => c.id === calendarId);
    return cal?.summary || calendarId || 'Não selecionado';
  };

  // -- Professional handlers --
  const handleAddProfessional = () => {
    if (!newProfName.trim() || !newProfCalendar) return;
    onChange('professionals', [
      ...professionals,
      { id: generateId(), name: newProfName.trim(), google_calendar_id: newProfCalendar, services: [], working_hours: null },
    ]);
    setNewProfName('');
    setNewProfCalendar('');
  };

  const handleDeleteProfessional = (profId) => {
    onChange('professionals', professionals.filter((p) => p.id !== profId));
  };

  // -- Shared styles --
  const sectionStyle = {
    background: '#f8fafc',
    borderRadius: '10px',
    border: '1px solid #e2e8f0',
    padding: '14px',
    marginBottom: '12px',
  };
  const labelStyle = { fontWeight: 500, fontSize: '13px', marginBottom: '6px', display: 'block' };
  const miniInputStyle = { width: '100%', padding: '6px 10px', border: '1px solid #dee2e6', borderRadius: '6px', fontSize: '13px', outline: 'none' };
  const miniButtonStyle = (variant = 'primary') => ({
    padding: '6px 12px',
    border: 'none',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    ...(variant === 'primary'
      ? { background: '#10b981', color: '#fff' }
      : variant === 'danger'
        ? { background: 'none', border: '1px solid #fca5a5', color: '#ef4444' }
        : { background: '#e2e8f0', color: '#475569' }),
  });
  const configureBtn = {
    padding: '5px 10px',
    fontSize: '12px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    background: '#fff',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    color: '#475569',
    whiteSpace: 'nowrap',
  };

  // -- No integration --
  if (!gcIntegration) {
    return (
      <div style={{ ...sectionStyle, background: '#fffbeb', borderColor: '#fde68a' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <IconAlertCircle size={18} style={{ color: '#d97706' }} />
          <small style={{ color: '#92400e' }}>
            Configure a integração <strong>Google Calendar</strong> na aba <strong>Integrações</strong> antes de usar esta opção.
          </small>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '16px' }}>
        <Spinner animation="border" size="sm" style={{ color: '#10b981' }} />
        <span style={{ marginLeft: '8px', color: '#666', fontSize: '13px' }}>Carregando calendários...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...sectionStyle, background: '#fff1f2', borderColor: '#fecaca' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <IconAlertCircle size={18} style={{ color: '#dc2626' }} />
          <span style={{ color: '#dc2626', fontSize: '13px', flex: 1 }}>{error}</span>
          <button type="button" onClick={fetchCalendars} style={{ ...miniButtonStyle('secondary'), fontSize: '11px' }}>
            <IconRefresh size={12} /> Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  // -- Status chips helper (inline, avoids recomputing in JSX) --
  const renderChips = (workingHours, svcList) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
        {(() => { const c = getHoursChip(workingHours); return (
          <>
            <IconClock size={12} style={{ color: c.active ? '#10b981' : '#94a3b8' }} />
            <span style={{ fontSize: '12px', color: c.active ? '#059669' : '#94a3b8', fontWeight: c.active ? 500 : 400 }}>{c.label}</span>
          </>
        ); })()}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
        {(() => { const c = getServicesChip(svcList); return (
          <>
            <IconBriefcase size={12} style={{ color: c.active ? '#10b981' : '#f59e0b' }} />
            <span style={{ fontSize: '12px', color: c.active ? '#059669' : '#f59e0b' }}>{c.label}</span>
          </>
        ); })()}
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '8px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <label style={{ ...labelStyle, marginBottom: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
          <IconCalendarEvent size={16} style={{ color: '#10b981' }} />
          Configuração do Google Calendar
        </label>
        <button type="button" onClick={fetchCalendars} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '3px' }}>
          <IconRefresh size={12} /> Atualizar
        </button>
      </div>

      {/* Mode toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
        <Form.Check
          type="switch"
          id="scp-multiple-calendars"
          label={<span><strong>Múltiplas agendas</strong> (profissionais separados)</span>}
          checked={useMultipleCalendars}
          onChange={(e) => onChange('use_multiple_calendars', e.target.checked)}
          style={{ fontSize: '13px' }}
        />
      </div>

      {/* -- SINGLE CALENDAR MODE -- */}
      {!useMultipleCalendars && (
        <div style={sectionStyle}>
          <label style={labelStyle}>Calendário</label>
          <Form.Select
            size="sm"
            value={config?.calendar_id || ''}
            onChange={(e) => onChange('calendar_id', e.target.value)}
            style={{ fontSize: '13px' }}
          >
            <option value="">Selecione um calendário...</option>
            {googleCalendars.map((cal) => (
              <option key={cal.id} value={cal.id}>
                {cal.summary || cal.id}{cal.primary ? ' (Principal)' : ''}
              </option>
            ))}
          </Form.Select>

          {config?.calendar_id && (
            <div style={{ borderTop: '1px solid #e2e8f0', marginTop: '14px', paddingTop: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
              {renderChips(config?.working_hours, services)}
              <button type="button" onClick={() => setHoursModalTarget('single')} style={configureBtn}>
                <IconSettings size={13} /> Configurar agenda
              </button>
            </div>
          )}
        </div>
      )}

      {/* -- MULTIPLE CALENDARS MODE -- */}
      {useMultipleCalendars && (
        <div style={sectionStyle}>
          <label style={labelStyle}>Profissionais ({professionals.length})</label>

          {/* Add professional form */}
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '10px' }}>
            <div style={{ flex: '1 1 120px' }}>
              <label style={{ ...labelStyle, fontSize: '11px' }}>Nome</label>
              <input type="text" value={newProfName} onChange={(e) => setNewProfName(e.target.value)} placeholder="Ex: Dr. Paulo" style={miniInputStyle} />
            </div>
            <div style={{ flex: '1 1 150px' }}>
              <label style={{ ...labelStyle, fontSize: '11px' }}>Calendário</label>
              <select value={newProfCalendar} onChange={(e) => setNewProfCalendar(e.target.value)} style={miniInputStyle}>
                <option value="">Selecione...</option>
                {googleCalendars.map((cal) => (
                  <option key={cal.id} value={cal.id}>{cal.summary || cal.id}{cal.primary ? ' (Principal)' : ''}</option>
                ))}
              </select>
            </div>
            <button type="button" onClick={handleAddProfessional} disabled={!newProfName.trim() || !newProfCalendar} style={miniButtonStyle('primary')}>
              <IconPlus size={14} /> Adicionar
            </button>
          </div>

          {/* Professionals list */}
          {professionals.map((prof) => (
            <div key={prof.id} style={{ background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '8px', overflow: 'hidden' }}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: '#f1f5f9' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                  <IconCalendarEvent size={14} style={{ color: '#10b981' }} />
                  <strong>{prof.name}</strong>
                  <span style={{ fontSize: '11px', color: '#94a3b8' }}>{getCalendarName(prof.google_calendar_id)}</span>
                </div>
                <button type="button" onClick={() => handleDeleteProfessional(prof.id)} style={{ ...miniButtonStyle('danger'), padding: '4px 6px' }}>
                  <IconTrash size={12} />
                </button>
              </div>

              {/* Status chips + configure button */}
              <div style={{ padding: '10px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                {renderChips(prof.working_hours, prof.services)}
                <button type="button" onClick={() => setHoursModalTarget(prof.id)} style={configureBtn}>
                  <IconSettings size={13} /> Configurar agenda
                </button>
              </div>
            </div>
          ))}

          {professionals.length === 0 && (
            <div style={{ fontSize: '12px', color: '#d97706', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <IconAlertCircle size={13} /> Adicione pelo menos um profissional
            </div>
          )}
        </div>
      )}

      {/* Working Hours + Services Modal — shared by single and multi-calendar modes */}
      <WorkingHoursModal
        show={hoursModalTarget !== null}
        onClose={() => setHoursModalTarget(null)}
        onSave={(newHours, newServices, newAgentSlotDuration, newAgentSlotEnabled) => {
          // Ensure all services have IDs (for node config storage)
          const servicesWithIds = newServices.map((s) => (s.id ? s : { ...s, id: generateId() }));
          const nextEnabled = newAgentSlotEnabled !== false;
          if (hoursModalTarget === 'single') {
            onChange('working_hours', newHours);
            onChange('services', servicesWithIds);
            onChange('agent_slot_duration', newAgentSlotDuration);
            onChange('agent_slot_enabled', nextEnabled);
          } else {
            onChange(
              'professionals',
              professionals.map((p) =>
                p.id === hoursModalTarget
                  ? {
                      ...p,
                      working_hours: newHours,
                      services: servicesWithIds,
                      agent_slot_duration: newAgentSlotDuration,
                      agent_slot_enabled: nextEnabled,
                    }
                  : p
              )
            );
          }
        }}
        calendarName={
          hoursModalTarget === 'single'
            ? getCalendarName(config?.calendar_id)
            : professionals.find((p) => p.id === hoursModalTarget)?.name || ''
        }
        initialValue={
          hoursModalTarget === 'single'
            ? config?.working_hours || null
            : professionals.find((p) => p.id === hoursModalTarget)?.working_hours || null
        }
        initialServices={
          hoursModalTarget === 'single'
            ? services
            : professionals.find((p) => p.id === hoursModalTarget)?.services || []
        }
        initialAgentSlotDuration={
          hoursModalTarget === 'single'
            ? (config?.agent_slot_duration ?? null)
            : (professionals.find((p) => p.id === hoursModalTarget)?.agent_slot_duration ?? null)
        }
        initialAgentSlotEnabled={
          hoursModalTarget === 'single'
            ? (config?.agent_slot_enabled !== false)
            : (professionals.find((p) => p.id === hoursModalTarget)?.agent_slot_enabled !== false)
        }
        globalSlotDuration={globalSlotDuration}
      />
    </div>
  );
};

export default SchedulingCalendarPicker;
